import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import Routes from 'routes';
import { KitOrder } from 'api/Responses/OrderResponses';
import Button from 'components/Button';
import { useLanguage } from 'context/AppContext';
import { formatDate } from 'utils/helpers/DateTimeFormat.helpers';
import { gsOneToSerial, mapStatusToIcon } from '../DeliveryPortal.helpers';
import { DropTableContainer } from '../DeliveryPortalPage.styles';

interface AllDeliveriesProps {
  data: KitOrder[];
  onFetchRows: () => Promise<void>;
  lastPage: boolean;
}

const AllDeliveries: React.FC<AllDeliveriesProps> = ({ data, onFetchRows, lastPage }) => {
  const [language] = useLanguage();

  const tableEl = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [bottom, setBottom] = useState(false);

  useEffect(() => {
    const loadItems = async () => {
      await onFetchRows();
      setLoading(false);
      setBottom(false);
    };

    if (bottom && !loading && !lastPage) {
      setLoading(true);
      loadItems();
    }
    /* eslint-disable */
    /* We explicitly only want this to run when bottom changes, adding loading would cause infinite render hell. We also only care about what the value of lastPage is when the effect is triggered by bottom changing, we don't want it to be triggered when lastPage changes. */
  }, [bottom]);

  useLayoutEffect(() => {
    const tableRef = tableEl.current;

    const scrollListener = () => {
      if (tableRef) {
        const { scrollTop, scrollHeight, clientHeight } = tableRef;

        // allow a 20px offset
        if (Math.ceil(scrollTop) + clientHeight >= scrollHeight - 20) {
          // reached the bottom, request more
          setBottom(true);
        }
      }
    };

    if (tableRef) {
      tableRef.addEventListener('scroll', scrollListener);
      return () => {
        tableRef.removeEventListener('scroll', scrollListener);
      };
    }
  }, []);

  return (
    <DropTableContainer ref={tableEl}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{language.DeliveryPortal.Table.deliveryNumber}</TableCell>
            <TableCell align="center">{language.DeliveryPortal.Table.kitCode}</TableCell>
            <TableCell align="center">{language.DeliveryPortal.Table.kitSerial}</TableCell>
            <TableCell align="center">{language.DeliveryPortal.Table.shippingDate}</TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.inspectionStatus}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length ? (
            data.map(kit => {
              return (
                <TableRow key={`${kit.order_number}-${kit.gs_one_barcode}`}>
                  <TableCell>{kit.order_number}</TableCell>
                  <TableCell align="center">{kit.display_code}</TableCell>
                  <TableCell align="center">{gsOneToSerial(kit.gs_one_barcode)}</TableCell>
                  <TableCell align="center">{formatDate(kit.shipping_date, false)}</TableCell>
                  <TableCell align="center" size="small">
                    {mapStatusToIcon(kit.status)}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell align="center" colSpan={5}>
                <Typography mb={4}>{language.DeliveryPortal.Table.noResults}</Typography>
                <Button startIcon={<FileUploadIcon />} href={Routes.DeliveryPortalUpload.path}>
                  {language.DeliveryPortal.Menu.Upload}
                </Button>
              </TableCell>
            </TableRow>
          )}
          {loading && (
            <TableRow>
              <TableCell align="center" colSpan={5}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </DropTableContainer>
  );
};

export default AllDeliveries;
